import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Loader2, MapPin, Check, X, CalendarDays, Film, Users, Sparkles, BedDouble, UtensilsCrossed, ChevronDown, Activity, FileDown, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PtfButton from "@/components/PtfButton";
import ProgrammeGallery from "@/components/ProgrammeGallery";
import JourneyReviews from "@/components/JourneyReviews";
import JourneyItinerary from "@/components/JourneyItinerary";
import JourneyRouteMap from "@/components/JourneyRouteMap";
import { WhatsAppIcon } from "@/components/ShareButtons";
import { LastHorizonKeyDates } from "@/components/LastHorizonKeyDates";
import { api, resolveImg, formatApiError } from "@/lib/api";
import { locationLabel, fmtJourneyDate, experiencePlace, TOURISM_PRINCIPLES } from "@/lib/content";
import { openCheckout } from "@/lib/payments";
import { trackMetaEvent } from "@/lib/meta";

import { ExpeditionMission, ExpeditionDossier } from "@/components/ExpeditionExtras";

function youtubeEmbedUrl(url) {
  if (!url) return "";
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : url;
}

const inputCls = "w-full bg-transparent border-b border-ptf-primary/25 py-2.5 text-sm outline-none transition-colors duration-300 focus:border-ptf-secondary placeholder:text-ptf-text/40 text-ptf-text";

const igUrl = (h) => (h ? (h.startsWith("http") ? h : `https://instagram.com/${h.replace(/^@/, "")}`) : "");
const socialUrl = (h) => (h && !h.startsWith("http") ? `https://${h}` : h);

function ApplicantCard({ a, highlight }) {
  return (
    <div data-testid={`last-horizon-applicant-${a.id}`} className="text-center">
      <div className={`aspect-square overflow-hidden border ${highlight ? "border-ptf-secondary/40" : "border-ptf-primary/12"} bg-ptf-primary/5`}>
        {a.profile_photo_url ? (
          <img src={resolveImg(a.profile_photo_url)} alt={a.name} className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="h-full w-full grid place-items-center text-ptf-text/30 text-[0.6rem] uppercase tracking-[0.14em]">No photo</div>
        )}
      </div>
      <p className="mt-2 text-xs font-semibold text-ptf-primary truncate">{a.name}</p>
      {(a.instagram || a.linkedin || a.facebook) && (
        <div className="flex items-center justify-center gap-2 mt-1 text-[0.6rem] uppercase tracking-[0.1em] text-ptf-secondary">
          {a.instagram && <a href={igUrl(a.instagram)} target="_blank" rel="noopener noreferrer" data-testid={`last-horizon-applicant-ig-${a.id}`} className="hover:text-ptf-primary">IG</a>}
          {a.linkedin && <a href={socialUrl(a.linkedin)} target="_blank" rel="noopener noreferrer" data-testid={`last-horizon-applicant-li-${a.id}`} className="hover:text-ptf-primary">LI</a>}
          {a.facebook && <a href={socialUrl(a.facebook)} target="_blank" rel="noopener noreferrer" data-testid={`last-horizon-applicant-fb-${a.id}`} className="hover:text-ptf-primary">FB</a>}
        </div>
      )}
    </div>
  );
}

function LastHorizonPublicSection() {
  const { data: status } = useQuery({ queryKey: ["last-horizon-status"], queryFn: async () => (await api.get("/last-horizon/status")).data });
  const { data: applicants = [] } = useQuery({ queryKey: ["last-horizon-applicants"], queryFn: async () => (await api.get("/last-horizon/applicants")).data });
  const { data: selected = [] } = useQuery({ queryKey: ["last-horizon-selected"], queryFn: async () => (await api.get("/last-horizon/selected")).data });
  return (
    <section data-testid="last-horizon-public-section" className="bg-white border-y border-ptf-primary/10 py-16 md:py-20">
      <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12">
        {status && <div className="mb-12"><LastHorizonKeyDates variant="muted" /></div>}
        {selected.length > 0 && (
          <div data-testid="last-horizon-selected-section" className="mb-16 border border-ptf-secondary/30 bg-ptf-secondary/[0.04] p-8">
            <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-secondary mb-3">Jury Decision</p>
            <h2 className="font-display font-extrabold uppercase text-2xl md:text-3xl tracking-tight text-ptf-primary mb-6">
              Selected Participants ({selected.length}/6)
            </h2>
            <div data-testid="last-horizon-selected-gallery" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
              {selected.map((a) => <ApplicantCard key={a.id} a={a} highlight />)}
            </div>
          </div>
        )}
        <div className="mb-10">
          <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-3">The Wildlife Filmmaking Challenge</p>
          <h2 className="font-display font-extrabold uppercase text-3xl md:text-4xl tracking-tight text-ptf-primary">Applicant Gallery</h2>
        </div>
        {applicants.length > 0 ? (
          <div data-testid="last-horizon-gallery" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {applicants.map((a) => <ApplicantCard key={a.id} a={a} />)}
          </div>
        ) : (
          <p data-testid="last-horizon-gallery-empty" className="text-sm italic font-serif-ed text-ptf-text/50">
            Applicants who consent to a public profile will appear here once their entry is received.
          </p>
        )}
      </div>
    </section>
  );
}

function JourneyRelatedMedia({ j }) {
  const { data: films = [] } = useQuery({ queryKey: ["films"], queryFn: async () => (await api.get("/films")).data });
  const { data: stories = [] } = useQuery({ queryKey: ["stories"], queryFn: async () => (await api.get("/stories")).data, enabled: (j.related_story_ids || []).length > 0 });
  const relFilms = films.filter((f) => f.expedition_title && f.expedition_title === j.title);
  const relStories = stories.filter((s) => (j.related_story_ids || []).includes(s.id));
  if (relFilms.length === 0 && relStories.length === 0) return null;
  return (
    <div data-testid="journey-related-media" className="mt-10 border border-ptf-primary/12 bg-white p-7">
      <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-4">From this expedition</p>
      <div className="flex flex-wrap gap-2">
        {relFilms.map((f) => (
          <Link key={f.slug} to={`/films/${f.slug}`} data-testid={`related-film-${f.slug}`} className="text-[0.65rem] uppercase tracking-[0.12em] border border-ptf-primary/30 text-ptf-primary px-3 py-1.5 hover:bg-ptf-primary hover:text-ptf-bg transition-colors">
            Film · {f.title}
          </Link>
        ))}
        {relStories.map((s) => (
          <Link key={s.id} to={`/stories/${s.id}`} data-testid={`related-story-${s.id}`} className="text-[0.65rem] uppercase tracking-[0.12em] border border-ptf-secondary/40 text-ptf-secondary px-3 py-1.5 hover:bg-ptf-secondary hover:text-white transition-colors">
            Story · {s.title}
          </Link>
        ))}
      </div>
    </div>
  );
}

const scrollToBooking = () => {
  const el = document.getElementById("journey-booking");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
};

function HeroCarousel({ images, title }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (images.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), 5000);
    return () => clearInterval(t);
  }, [images.length]);
  return (
    <>
      <AnimatePresence initial={false}>
        <motion.img
          key={idx}
          src={resolveImg(images[idx])}
          alt={title}
          data-testid="hero-carousel-image"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
          className="absolute inset-0 h-full w-full object-cover"
        />
      </AnimatePresence>
      <div className="absolute inset-0 bg-gradient-to-t from-ptf-primary via-ptf-primary/70 to-ptf-primary/40" />
      {images.length > 1 && (
        <div data-testid="hero-carousel-dots" className="absolute bottom-6 right-6 z-10 flex items-center gap-2">
          {images.map((img, i) => (
            <button
              key={`${img}-${i}`}
              type="button"
              aria-label={`Photo ${i + 1}`}
              data-testid={`hero-carousel-dot-${i}`}
              onClick={() => setIdx(i)}
              className={`h-1.5 transition-all duration-500 ${i === idx ? "w-7 bg-ptf-accent" : "w-3 bg-ptf-bg/40 hover:bg-ptf-bg/70"}`}
            />
          ))}
        </div>
      )}
    </>
  );
}

function Section({ id, title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-ptf-primary/12">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid={`section-${id}`}
        className="w-full flex items-center justify-between gap-4 py-6 text-left group"
      >
        <h2 className="font-display uppercase text-2xl md:text-3xl tracking-tight text-ptf-primary group-hover:text-ptf-secondary transition-colors">{title}</h2>
        <ChevronDown className={`h-5 w-5 shrink-0 text-ptf-secondary transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-10">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Fact({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[0.6rem] uppercase tracking-[0.2em] text-ptf-text/50">{label}</p>
      <p className="font-display uppercase text-lg md:text-xl tracking-tight text-ptf-primary mt-1">{value}</p>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="faq-item" className="py-4">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="faq-question-toggle"
        className="w-full flex items-center justify-between gap-4 text-left group"
      >
        <span className="text-sm font-semibold text-ptf-primary group-hover:text-ptf-secondary transition-colors">{q}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-ptf-secondary transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p data-testid="faq-answer" className="mt-3 text-sm text-ptf-text/70 leading-relaxed">{a}</p>}
    </div>
  );
}

function FactsBar({ j }) {
  const announced = (j.seats_total || 0) === 0;
  const isLastHorizon = j.title === "The Last Horizon";
  const isDawn = j.title === "Dawn: A Project of Belief";
  return (
    <section data-testid="journey-facts-bar" className="bg-white border-b border-ptf-primary/12 sticky top-16 z-30">
      <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12 py-4 md:py-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex flex-1 min-w-0 flex-wrap items-center gap-x-8 md:gap-x-10 gap-y-4">
          <Fact testid="fact-duration" label="Duration" value={j.duration_days > 0 ? `${j.duration_days} Days` : "TBA"} />
          <Fact testid="fact-type" label="Format" value={isLastHorizon ? "Wildlife Filmmaking Challenge" : isDawn ? "Documentary Selection Journey" : j.is_flagship ? "Documentary Expedition" : "Field Experience"} />
          {j.activity_level && <Fact testid="fact-activity" label="Activity Level" value={j.activity_level} />}
          <Fact testid="fact-group" label="Participation" value={isLastHorizon ? "6 Selected Filmmakers" : isDawn ? "24 Selected Participants" : announced ? "TBA" : `Limited to ${j.seats_total}`} />
        </div>
        <button
          type="button"
          onClick={scrollToBooking}
          data-testid="facts-reserve-btn"
          className="w-full md:w-auto shrink-0 inline-flex items-center justify-center bg-ptf-primary text-ptf-bg px-4 md:px-6 py-3 text-[0.65rem] md:text-[0.7rem] font-semibold uppercase tracking-[0.14em] md:tracking-[0.18em] hover:bg-ptf-secondary transition-colors"
        >
          {isLastHorizon ? "Apply for The Last Horizon" : isDawn ? "Apply for DAWN" : announced ? "Register Interest" : "Apply for this Experience"}
        </button>
      </div>
    </section>
  );
}

function ParticipationCard({ j, onBooked }) {
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });
  const waPhone = (settings?.phone || "").replace(/[^\d]/g, "");
  const isLastHorizon = j.title === "The Last Horizon";
  const isDawn = j.title === "Dawn: A Project of Belief";
  const announced = (j.seats_total || 0) === 0;
  const seatsLeft = Math.max((j.seats_total || 0) - (j.seats_booked || 0), 0);
  const bookable = ["OPEN", "UPCOMING"].includes(j.status) && (announced || seatsLeft > 0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", seats: 1, message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/journeys/${j.id}/book`, { ...form, seats: announced ? 1 : Number(form.seats) || 1 });
      if (!data.payment) {
        setDone({ paid: false, message: data.message });
        return;
      }
      const resp = await openCheckout({
        keyId: data.key_id, orderId: data.order_id, amount: data.amount, currency: data.currency,
        description: j.title,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        tracking: { content_name: j.title, content_type: "expedition", product_type: "expedition", programme_id: j.id },
      });
      const v = await api.post("/bookings/verify", {
        razorpay_order_id: resp.razorpay_order_id,
        razorpay_payment_id: resp.razorpay_payment_id,
        razorpay_signature: resp.razorpay_signature,
        fbp: resp.fbp, fbc: resp.fbc,
      });
      setDone({ paid: true, message: v.data.message });
      onBooked();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail ? formatApiError(detail) : (err?.message || "Could not complete the application. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  if (isLastHorizon) {
    return (
      <div id="journey-booking" data-testid="last-horizon-apply-card" className="sticky top-[170px] border border-ptf-primary/12 p-7 bg-white">
        <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-4">Wildlife Filmmaking Challenge</p>
        <p className="font-display uppercase text-2xl tracking-tight text-ptf-primary">₹5,000 Application Fee</p>
        <dl className="mt-5 space-y-3 text-sm border-t border-ptf-primary/10 pt-5">
          <div className="flex items-center gap-2 text-ptf-text/75">
            <CalendarDays className="h-4 w-4 text-ptf-secondary" /> 10 to 30 October 2026
          </div>
          <div className="flex items-center gap-2 text-ptf-text/75">
            <Users className="h-4 w-4 text-ptf-secondary" /> 6 filmmakers selected by the Foundation jury
          </div>
        </dl>
        <p className="mt-5 text-sm text-ptf-text/70 leading-relaxed">
          This is not a paid expedition booking. Submit ten wildlife photographs and a
          30-second wildlife video for jury review, and pay the application fee to enter
          the challenge. Payment does not guarantee selection.
        </p>
        <PtfButton to="/last-horizon/apply" data-testid="last-horizon-apply-btn" className="mt-6 w-full justify-center">
          Apply for The Last Horizon
        </PtfButton>
        {waPhone && (
          <a
            href={`https://wa.me/${waPhone}?text=${encodeURIComponent(`Hi Planet Trotter Foundation, I would like to know more about "The Last Horizon".`)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-enquiry-btn"
            className="mt-3 flex items-center justify-center gap-2 border border-[#1FA855]/40 bg-[#1FA855]/[0.06] px-5 py-3 text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-[#128C4B] hover:bg-[#1FA855] hover:text-white transition-colors"
          >
            <WhatsAppIcon className="h-4 w-4" /> Request Details on WhatsApp
          </a>
        )}
      </div>
    );
  }

  if (isDawn) {
    return (
      <div id="journey-booking" data-testid="dawn-apply-card" className="sticky top-[170px] border border-ptf-primary/12 p-7 bg-white">
        <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-4">Documentary Selection Journey</p>
        <p className="font-display uppercase text-2xl tracking-tight text-ptf-primary">₹499 Application Fee</p>
        <dl className="mt-5 space-y-3 text-sm border-t border-ptf-primary/10 pt-5">
          <div className="flex items-center gap-2 text-ptf-text/75">
            <CalendarDays className="h-4 w-4 text-ptf-secondary" /> 27 February to 13 March 2027
          </div>
          <div className="flex items-center gap-2 text-ptf-text/75">
            <Users className="h-4 w-4 text-ptf-secondary" /> 24 participants selected by the Planet Trotter team
          </div>
        </dl>
        <p className="mt-5 text-sm text-ptf-text/70 leading-relaxed">
          This is not a paid expedition booking. Submitting the DAWN application and
          paying the ₹499 fee enters you into the selection process. Payment does not
          guarantee shortlisting, interview or selection. The fee is non-refundable.
        </p>
        <PtfButton to="/dawn/apply" data-testid="dawn-apply-btn" className="mt-6 w-full justify-center">
          Apply for DAWN
        </PtfButton>
        {waPhone && (
          <a
            href={`https://wa.me/${waPhone}?text=${encodeURIComponent(`Hi Planet Trotter Foundation, I would like to know more about "DAWN: A Project of Belief".`)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-enquiry-btn"
            className="mt-3 flex items-center justify-center gap-2 border border-[#1FA855]/40 bg-[#1FA855]/[0.06] px-5 py-3 text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-[#128C4B] hover:bg-[#1FA855] hover:text-white transition-colors"
          >
            <WhatsAppIcon className="h-4 w-4" /> Request Details on WhatsApp
          </a>
        )}
      </div>
    );
  }

  return (
    <div id="journey-booking" data-testid="booking-card" className="sticky top-[170px] border border-ptf-primary/12 p-7 bg-white">
      <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-4">
        {announced ? "Register interest" : "Participation"}
      </p>
      {j.price > 0 ? (
        <div>
          <p className="text-[0.6rem] uppercase tracking-[0.2em] text-ptf-text/50 mb-1.5">Expedition Contribution</p>
          <div className="flex items-baseline gap-2">
            <span data-testid="journey-price" className="font-display text-4xl text-ptf-primary">₹{Number(j.price || 0).toLocaleString("en-IN")}</span>
            <span className="text-xs text-ptf-text/55">per participant</span>
          </div>
          {j.advance_amount > 0 && (
            <p data-testid="journey-advance" className="mt-1.5 text-xs text-ptf-secondary">Applications are confirmed with a ₹{Number(j.advance_amount).toLocaleString("en-IN")} advance per place</p>
          )}
        </div>
      ) : (
        <p data-testid="journey-price-tba" className="font-display text-2xl text-ptf-primary">Contribution to be announced</p>
      )}
      <dl className="mt-5 space-y-3 text-sm border-t border-ptf-primary/10 pt-5">
        {j.start_date ? (
          <div className="flex items-center gap-2 text-ptf-text/75">
            <CalendarDays className="h-4 w-4 text-ptf-secondary" />
            {fmtJourneyDate(j.start_date)}{j.end_date ? ` to ${fmtJourneyDate(j.end_date)}` : ""}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-ptf-text/75">
            <CalendarDays className="h-4 w-4 text-ptf-secondary" /> Dates to be announced
          </div>
        )}
        <div className="flex items-center gap-2 text-ptf-text/75">
          <Users className="h-4 w-4 text-ptf-secondary" />
          <span data-testid="journey-seats-left">
            {announced ? "Registrations open" : `Limited participation · ${seatsLeft} ${seatsLeft === 1 ? "place" : "places"} remaining`}
          </span>
        </div>
        {j.activity_level && (
          <div className="flex items-center gap-2 text-ptf-text/75">
            <Activity className="h-4 w-4 text-ptf-secondary" /> {j.activity_level}
          </div>
        )}
        {j.accommodation && (
          <div className="flex items-start gap-2 text-ptf-text/75">
            <BedDouble className="h-4 w-4 mt-0.5 shrink-0 text-ptf-secondary" /> {j.accommodation}
          </div>
        )}
        {j.meals && (
          <div className="flex items-start gap-2 text-ptf-text/75">
            <UtensilsCrossed className="h-4 w-4 mt-0.5 shrink-0 text-ptf-secondary" /> {j.meals}
          </div>
        )}
      </dl>

      {j.brochure_url && (
        <a
          href={resolveImg(j.brochure_url)}
          target="_blank"
          rel="noopener noreferrer"
          download
          data-testid="brochure-download-btn"
          className="mt-5 flex items-center justify-center gap-2 border border-ptf-primary/25 px-5 py-3 text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-ptf-primary hover:bg-ptf-primary hover:text-ptf-bg transition-colors"
        >
          <FileDown className="h-4 w-4" /> Download the Expedition Dossier (PDF)
        </a>
      )}

      {waPhone && (
        <a
          href={`https://wa.me/${waPhone}?text=${encodeURIComponent(`Hi Planet Trotter Foundation, I would like to know more about "${j.title}".`)}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="whatsapp-enquiry-btn"
          className="mt-3 flex items-center justify-center gap-2 border border-[#1FA855]/40 bg-[#1FA855]/[0.06] px-5 py-3 text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-[#128C4B] hover:bg-[#1FA855] hover:text-white transition-colors"
        >
          <WhatsAppIcon className="h-4 w-4" /> Request Details on WhatsApp
        </a>
      )}

      {done ? (
        <div data-testid="booking-success" className="mt-6 border-t border-ptf-primary/10 pt-6">
          <span className="grid place-items-center h-11 w-11 rounded-full bg-ptf-accent text-ptf-primary mb-4"><Check className="h-5 w-5" /></span>
          <h4 className="font-display uppercase text-xl tracking-tight text-ptf-primary">{done.paid ? "Application confirmed." : "Application received."}</h4>
          <p className="mt-2 text-sm text-ptf-text/75 leading-relaxed">{done.message}</p>
        </div>
      ) : bookable ? (
        <form onSubmit={submit} data-testid="booking-form" className="mt-6 border-t border-ptf-primary/10 pt-5 space-y-4">
          <p className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-primary/50">
            {announced ? "Register your interest" : "Apply for this experience"}
          </p>
          {announced && (
            <p data-testid="announced-note" className="text-xs text-ptf-text/60 leading-relaxed">
              Full dates, itinerary and contribution are being finalised. Register your
              interest and you will hear the details first, with no obligation.
            </p>
          )}
          <input required data-testid="bk-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name *" />
          <input required type="email" data-testid="bk-email" value={form.email} onChange={set("email")} className={inputCls} placeholder="Email *" />
          <input data-testid="bk-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="Phone" />
          {!announced && (
            <label className="block">
              <span className="text-[0.65rem] uppercase tracking-[0.16em] text-ptf-text/55">Places</span>
              <select data-testid="bk-seats" value={form.seats} onChange={set("seats")} className={inputCls}>
                {Array.from({ length: Math.max(seatsLeft, 1) }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
          )}
          <textarea rows={2} data-testid="bk-message" value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Your background or interest in the field" />
          <PtfButton type="submit" disabled={loading} data-testid="bk-submit" className="w-full justify-center">
            {loading ? "Processing" : announced ? "Register Interest" : "Apply for this Experience"}
          </PtfButton>
          {!announced && (
            <p className="text-[0.65rem] text-ptf-text/50 leading-relaxed">
              {j.advance_amount > 0 ? (
                <>Payable now: ₹{(Number(j.advance_amount) * (Number(form.seats) || 1)).toLocaleString("en-IN")} advance (₹{Number(j.advance_amount).toLocaleString("en-IN")}/place). Balance by bank transfer after confirmation; full payment due 90 days before departure. </>
              ) : (
                <>Total contribution: ₹{(Number(j.price || 0) * (Number(form.seats) || 1)).toLocaleString("en-IN")}. </>
              )}
              The contribution covers your participation: flights, permits, camps and field
              logistics. It is not a donation, and no part of it is presented as one.
              Secure payment via Razorpay when online payments are live; until then we
              record your application and confirm by email.
            </p>
          )}
        </form>
      ) : (
        <p data-testid="booking-closed" className="mt-6 border-t border-ptf-primary/10 pt-5 text-sm text-ptf-text/60 leading-relaxed">
          {seatsLeft === 0 && !announced ? "Participation for this experience is closed." : "Applications are not open for this experience."} Write to us via the <Link to="/contact" className="underline underline-offset-2 text-ptf-primary">contact page</Link> to join the waitlist.
        </p>
      )}
    </div>
  );
}

export default function JourneyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: j, isLoading, isError } = useQuery({
    queryKey: ["journey", id],
    queryFn: async () => (await api.get(`/journeys/${id}`)).data,
    retry: false,
  });

  useEffect(() => {
    if (!j) return;
    trackMetaEvent("ViewContent", {
      content_name: j.title,
      content_type: j.title === "The Last Horizon" ? "competition" : "expedition",
      content_ids: [j.id],
    });
  }, [j]);

  if (isLoading) return <div className="min-h-screen grid place-items-center bg-ptf-bg"><Loader2 className="h-8 w-8 animate-spin text-ptf-primary" /></div>;
  if (isError || !j) {
    return (
      <div className="min-h-screen grid place-items-center bg-ptf-bg text-center px-6">
        <div>
          <h1 className="font-display uppercase text-4xl text-ptf-primary">Experience not found</h1>
          <div className="mt-6 flex justify-center"><PtfButton onClick={() => navigate("/exploration")}>Back to Field Experiences</PtfButton></div>
        </div>
      </div>
    );
  }

  const announced = (j.seats_total || 0) === 0;
  const isDawn = j.title === "Dawn: A Project of Belief";
  const cover = j.images?.[0];
  const galleryItems = j.gallery?.length ? j.gallery : (j.images || []).slice(1).map((url) => ({ url, caption: "" }));
  const hasDays = j.days?.length > 0;
  const place = experiencePlace(j);
  const heroLocationLine = [place, locationLabel({ ...j, country: "" })].filter(Boolean).join(" · ");

  return (
    <>
      <Seo
        title={j.seo_title || j.title}
        path={`/expeditions/${id}`}
        description={j.seo_description || j.mission || j.purpose || j.summary || `A Planet Trotter expedition${place ? ` in ${place.split(" · ")[0]}` : ""}.`}
        image={cover ? resolveImg(cover) : undefined}
      />

      <section className="relative bg-ptf-primary text-ptf-bg overflow-hidden grain">
        {cover && <HeroCarousel images={j.images} title={j.title} />}
        <div className="relative z-10 mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12 pt-40 pb-16 md:pt-48 md:pb-20">
          <Link to="/expeditions" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-ptf-bg/70 hover:text-ptf-accent transition-colors mb-8">
            <ArrowLeft className="h-4 w-4" /> All Expeditions
          </Link>
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <span data-testid="journey-format" className="text-[0.6rem] uppercase tracking-[0.16em] px-3 py-1.5 bg-ptf-accent text-ptf-primary">Planet Trotter Expedition</span>
            {j.is_flagship && (
              <span data-testid="journey-flagship-badge" className="inline-flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.16em] bg-ptf-bg text-ptf-primary px-3 py-1.5">
                <Film className="h-3.5 w-3.5" /> Documentary Expedition
              </span>
            )}
            <span data-testid="journey-status" className="text-[0.6rem] uppercase tracking-[0.16em] border border-ptf-bg/25 px-3 py-1.5 text-ptf-bg/70">
              {j.duration_days > 0 ? `${j.duration_days} Days` : "Dates to be announced"}
            </span>
          </div>
          <motion.h1
            data-testid="journey-title"
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="font-display font-extrabold uppercase text-5xl md:text-7xl leading-[0.92] tracking-tight max-w-4xl"
          >
            {j.title}
          </motion.h1>
          <p className="mt-6 inline-flex items-center gap-2 text-ptf-bg/80 uppercase tracking-[0.18em] text-sm">
            <MapPin className="h-4 w-4 text-ptf-accent" />
            {heroLocationLine}
          </p>
          {isDawn && (
            <Link
              to="/films/dawn-a-project-of-belief"
              data-testid="journey-watch-film-link"
              className="mt-6 inline-flex items-center gap-2 text-sm uppercase tracking-[0.16em] text-ptf-accent hover:text-ptf-bg transition-colors underline underline-offset-4"
            >
              <Film className="h-4 w-4" /> Watch the Film
            </Link>
          )}
        </div>
      </section>

      <FactsBar j={j} />

      {j.video_url && (
        <section className="bg-ptf-bg py-14 md:py-16 border-b border-ptf-primary/10">
          <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12">
            <div className="aspect-video border border-ptf-primary/12 bg-ptf-primary" data-testid="journey-video-player">
              <iframe src={youtubeEmbedUrl(j.video_url)} title={j.title} className="h-full w-full" allowFullScreen loading="lazy" />
            </div>
          </div>
        </section>
      )}

      <section className="bg-ptf-bg py-14 md:py-20">
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <div className="border-t border-ptf-primary/12">
              <ExpeditionMission j={j} />
              <Section id="purpose" title="Why This Experience Exists" defaultOpen>
                {j.summary && <p className="font-serif-ed italic text-2xl text-ptf-secondary leading-snug mb-6">{j.summary}</p>}
                {j.purpose ? (
                  <p data-testid="journey-purpose" className="text-lg text-ptf-text/80 leading-relaxed whitespace-pre-line">{j.purpose}</p>
                ) : (
                  j.description && <p className="text-lg text-ptf-text/80 leading-relaxed whitespace-pre-line">{j.description}</p>
                )}
              </Section>

              {(j.story || (j.purpose && j.description)) && (
                <Section id="story" title="The Story" defaultOpen>
                  {j.story && <p data-testid="journey-story" className="text-lg text-ptf-text/80 leading-relaxed whitespace-pre-line">{j.story}</p>}
                  {j.purpose && j.description && <p className={`text-ptf-text/75 leading-relaxed whitespace-pre-line ${j.story ? "mt-6 text-sm" : "text-lg"}`}>{j.description}</p>}
                </Section>
              )}

              {(hasDays || j.itinerary?.length > 0 || j.route?.length >= 2 || j.highlights?.length > 0) && (
                <Section id="field-journey" title={isDawn ? "The Process" : "The Field Journey"} defaultOpen>
                  {j.route?.length >= 2 && (
                    <div className="mb-8">
                      <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-4">The route</p>
                      <JourneyRouteMap route={j.route} />
                    </div>
                  )}
                  {j.highlights?.length > 0 && (
                    <div className="mb-8">
                      <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-4">{isDawn ? "What do you believe in?" : "Field notes"}</p>
                      <ul data-testid="journey-highlights" className="space-y-2.5">
                        {j.highlights.map((h) => (
                          <li key={h} className="flex items-start gap-2.5 text-ptf-text/80">
                            <Sparkles className="h-4 w-4 mt-1 shrink-0 text-ptf-secondary" /> {h}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {hasDays ? (
                    <JourneyItinerary days={j.days} dayLabel={isDawn ? "Step" : j.title === "The Last Horizon" ? "Phase" : "Day"} />
                  ) : j.itinerary?.length > 0 ? (
                    <ol data-testid="journey-itinerary" className="space-y-4">
                      {j.itinerary.map((step, i) => (
                        <li key={step} className="flex items-start gap-4">
                          <span className="shrink-0 grid place-items-center h-8 w-8 border border-ptf-primary/20 font-display text-sm text-ptf-primary">{String(i + 1).padStart(2, "0")}</span>
                          <p className="text-ptf-text/80 leading-relaxed pt-1">{step}</p>
                        </li>
                      ))}
                    </ol>
                  ) : null}
                  {j.activities?.length > 0 && (
                    <div className="mt-8">
                      <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-4">In the field</p>
                      <div data-testid="journey-activities" className="flex flex-wrap gap-2">
                        {j.activities.map((a) => (
                          <span key={a} className="text-[0.7rem] uppercase tracking-[0.12em] border border-ptf-primary/25 text-ptf-text/75 px-3.5 py-1.5">{a}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </Section>
              )}

              {j.conservation_context && (
                <Section id="conservation" title="Conservation Context">
                  <p data-testid="journey-conservation" className="text-lg text-ptf-text/80 leading-relaxed whitespace-pre-line">{j.conservation_context}</p>
                </Section>
              )}

              {(j.photography_note || (j.is_flagship && j.flagship_note)) && (
                <Section id="photography" title="Photography & Documentary">
                  {j.photography_note && <p data-testid="journey-photography" className="text-lg text-ptf-text/80 leading-relaxed whitespace-pre-line">{j.photography_note}</p>}
                  {j.is_flagship && j.flagship_note && (
                    <div className={j.photography_note ? "mt-8 border-l-2 border-ptf-accent pl-6" : ""}>
                      <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-3 flex items-center gap-2"><Film className="h-4 w-4" /> The film</p>
                      <p className="text-ptf-text/80 leading-relaxed whitespace-pre-line">{j.flagship_note}</p>
                      <Link to="/flagship" className="mt-4 inline-block text-xs uppercase tracking-[0.16em] underline underline-offset-4 text-ptf-primary hover:text-ptf-secondary">How documentary expeditions work</Link>
                    </div>
                  )}
                </Section>
              )}

              <Section id="responsible" title="Responsible Exploration">
                {j.responsible_note ? (
                  <p data-testid="journey-responsible" className="text-lg text-ptf-text/80 leading-relaxed whitespace-pre-line">{j.responsible_note}</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {TOURISM_PRINCIPLES.map((p) => (
                      <div key={p.num} className="border border-ptf-primary/12 bg-white p-5">
                        <p className="font-display text-2xl text-ptf-primary/20">{p.num}</p>
                        <h4 className="font-display uppercase text-lg tracking-tight text-ptf-primary mt-1">{p.title}</h4>
                        <p className="mt-2 text-sm text-ptf-text/70 leading-relaxed">{p.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              {j.who_for?.length > 0 && (
                <Section id="who-for" title="Who This Is For">
                  <ul data-testid="journey-who-for" className="space-y-2.5">
                    {j.who_for.map((w) => (
                      <li key={w} className="flex items-start gap-2.5 text-ptf-text/80">
                        <Users className="h-4 w-4 mt-1 shrink-0 text-ptf-secondary" /> {w}
                      </li>
                    ))}
                  </ul>
                  {j.group_note && (
                    <p data-testid="journey-group-note" className="mt-5 border border-ptf-secondary/25 bg-ptf-secondary/[0.04] p-4 text-sm text-ptf-text/75 leading-relaxed">
                      {j.group_note}
                    </p>
                  )}
                </Section>
              )}

              {j.includes?.length > 0 && (
                <Section id="included" title="What Is Included">
                  <ul data-testid="journey-includes" className="space-y-2.5">
                    {j.includes.map((a) => (
                      <li key={a} className="flex items-start gap-2.5 text-ptf-text/80">
                        <Check className="h-4 w-4 mt-1 shrink-0 text-ptf-secondary" /> {a}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {j.excludes?.length > 0 && (
                <Section id="not-included" title="What Is Not Included">
                  <ul data-testid="journey-excludes" className="space-y-2.5">
                    {j.excludes.map((a) => (
                      <li key={a} className="flex items-start gap-2.5 text-ptf-text/70">
                        <X className="h-4 w-4 mt-1 shrink-0 text-ptf-primary/40" /> {a}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              <Section id="dates-prices" title="Dates & Participation">
                {announced ? (
                  <p data-testid="dates-tba" className="text-ptf-text/75 leading-relaxed">
                    Departure dates and the field experience contribution are being finalised.
                    Register your interest and you will receive the full details before they
                    are published here.
                  </p>
                ) : (
                  <div data-testid="departure-row" className="border border-ptf-primary/12 bg-white p-6 flex flex-wrap items-center gap-x-10 gap-y-4">
                    <div>
                      <p className="text-[0.6rem] uppercase tracking-[0.2em] text-ptf-text/50">Dates</p>
                      <p className="font-display uppercase text-xl tracking-tight text-ptf-primary mt-1 inline-flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-ptf-secondary" />
                        {j.start_date ? `${fmtJourneyDate(j.start_date)}${j.end_date ? ` to ${fmtJourneyDate(j.end_date)}` : ""}` : "To be announced"}
                      </p>
                    </div>
                    {!isDawn && (
                      <div>
                        <p className="text-[0.6rem] uppercase tracking-[0.2em] text-ptf-text/50">Contribution</p>
                        <p className="font-display uppercase text-xl tracking-tight text-ptf-primary mt-1">₹{Number(j.price || 0).toLocaleString("en-IN")}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-[0.6rem] uppercase tracking-[0.2em] text-ptf-text/50">Participation</p>
                      <p className="font-display uppercase text-xl tracking-tight text-ptf-primary mt-1">{isDawn ? "24 Selected" : `${Math.max((j.seats_total || 0) - (j.seats_booked || 0), 0)} places remaining`}</p>
                    </div>
                    <button
                      type="button"
                      onClick={scrollToBooking}
                      data-testid="departure-reserve-btn"
                      className="ml-auto inline-flex items-center bg-ptf-primary text-ptf-bg px-6 py-3 text-[0.7rem] font-semibold uppercase tracking-[0.18em] hover:bg-ptf-secondary transition-colors"
                    >
                      Apply
                    </button>
                  </div>
                )}
                <p className="mt-4 text-[0.65rem] text-ptf-text/50 leading-relaxed">
                  {isDawn
                    ? "The ₹499 application fee is for entering the DAWN selection process. It does not guarantee shortlisting, interview, selection or participation, and is non-refundable."
                    : "The contribution is per participant, in Indian rupees, and covers participation in the expedition. It is not a donation. Places are confirmed on payment once online payments are live; until then applications are confirmed by email."}
                </p>
              </Section>

              {j.faq?.length > 0 && (
                <Section id="faq" title="Frequently Asked Questions">
                  <div className="divide-y divide-ptf-primary/10">
                    {j.faq.map((f, i) => <FaqItem key={f.q || i} q={f.q} a={f.a} />)}
                  </div>
                </Section>
              )}
            </div>

            <ExpeditionDossier j={j} hideApplicationForm={isDawn} />

            {j.training_links?.length > 0 && (
              <div data-testid="journey-training" className="mt-10 border border-ptf-primary/12 bg-white p-7">
                <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-3 flex items-center gap-2">
                  <GraduationCap className="h-4 w-4" /> Related learning
                </p>
                <p className="text-sm text-ptf-text/70 leading-relaxed">
                  This experience connects with themes planned for Planet Trotter Learning & Training.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {j.training_links.map((t) => (
                    <Link key={t} to={`/learning/training?q=${encodeURIComponent(t)}`} className="text-[0.65rem] uppercase tracking-[0.12em] border border-ptf-secondary/40 text-ptf-secondary px-3 py-1.5 hover:bg-ptf-secondary hover:text-white transition-colors">{t}</Link>
                  ))}
                </div>
              </div>
            )}

            <JourneyRelatedMedia j={j} />

            <JourneyReviews journeyId={j.id} />

            {j.video_gallery?.length > 0 && (
              <div className="pt-10" data-testid="journey-video-gallery">
                <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-4">More from the field</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {j.video_gallery.map((v, i) => (
                    <div key={v.url || i} data-testid={`journey-gallery-video-${i}`}>
                      <div className="aspect-video border border-ptf-primary/12 bg-ptf-primary overflow-hidden">
                        <iframe src={youtubeEmbedUrl(v.url)} title={v.caption || `${j.title} clip ${i + 1}`} className="h-full w-full" allowFullScreen loading="lazy" />
                      </div>
                      {v.caption && <p className="mt-2.5 text-sm text-ptf-text/70 leading-snug">{v.caption}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {galleryItems.length > 0 && (
              <div className="pt-10">
                <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-4">From the field</p>
                <ProgrammeGallery items={galleryItems} />
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <ParticipationCard j={j} onBooked={() => qc.invalidateQueries({ queryKey: ["journey", id] })} />
          </aside>
        </div>
      </section>

      {j.title === "The Last Horizon" && <LastHorizonPublicSection />}
    </>
  );
}
